/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Icon } from '../../common/Icon/Icon';
import Loader from '../../common/Loader/Loader';
import ToggleButton from '../../common/ToggleButton/ToggleButton';
import ToggleButtonItem from '../../common/ToggleButton/ToggleButtonItem/ToggleButtonItem';
import { useScreenSizeContext } from '../../core/context/screenSize.context';
import { useUserContext } from '../../core/context/user.context';
import { buildParams, geoJsonToLatLng, isValidEmail, toBool } from '../../core/helpers';
import { useFormInput } from '../../core/hooks/useFormInput';
import { AssessChargersResponse, Charger, ChargerAssessment, Hotel } from '../../core/models';
import GoogleMap from '../GoogleMap';
import Marker from '../Marker';
import './AdminMap.css';
import AdminSearchFilter from './AdminSearchFilter';
import AdminSupplierInfo from './AdminSupplierInfo';

const CHARGER_DISTANCE_RADIUS = 250;
const REGION_ZOOM_LEVEL = 9;
const PROPERTY_ZOOM_LEVEL = 19;
const MIN_MAP_ZOOM = 3;
const MAX_MAP_ZOOM = 21;
const DEFAULT_LATLNG = {
  lat: 52,
  lng: 5,
};
export const PROPERTY_FILTER = {
  all: 'all',
  unconfirmed: 'unconfirmed',
  confirmed: 'confirmed',
};

export default function AdminMap() {
  // const [ map, setMap ] = useState();
  const [searchParams] = useSearchParams();
  const { isMobile } = useScreenSizeContext();
  const abortController = useRef<any>(null);
  const [latspan, setLatspan] = useState(0);
  const [lngspan, setLngspan] = useState(0);

  const queryParams = {
    sid: searchParams.get('sid'),
    max_distance: Number(searchParams.get('max_distance')),
    use_amenity: toBool(searchParams.get('use_amenity')),
    has_amenity: toBool(searchParams.get('has_amenity')),
    use_ecomov: toBool(searchParams.get('use_ecomov')),
    hide_confirmed: toBool(searchParams.get('hide_confirmed')),
    all_ratings: toBool(searchParams.get('all_ratings')),
    country_id: searchParams.get('country_id'),
    province_id: searchParams.get('province_id'),
    lat: searchParams.get('lat'),
    lng: searchParams.get('lng'),
    only_empty_email: toBool(searchParams.get('only_empty_email')),
    min_rating: Number(searchParams.get('min_rating')),
    prev_cursor: searchParams.get('prev_cursor'),
    next_cursor: searchParams.get('next_cursor'),
    latspan,
    lngspan,
  };
  const authenticationToken = sessionStorage.getItem('authenticationToken');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<Hotel[]>([]);
  const [selectedChargers, setSelectedChargers] = useState<Charger[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [mapZoom, setMapZoom] = useState(REGION_ZOOM_LEVEL);
  const [mapCenter, setMapCenter] = useState(DEFAULT_LATLNG);
  const [regionCenter, setRegionCenter] = useState(DEFAULT_LATLNG);
  // const [note, setNote] = useState('');
  // const [email, setEmail] = useState('');
  const note = useFormInput('', () => true);
  const email = useFormInput('', () => true);
  const { user } = useUserContext();
  const [groupedItems, setGroupedItems] = useState<{
    all: Hotel[];
    unconfirmed: Hotel[];
    confirmed: Hotel[];
  }>({
    all: [],
    unconfirmed: [],
    confirmed: [],
  });
  const [propertyFilter, setPropertyFilter] = useState<string>(PROPERTY_FILTER.all);
  const [showNote, setShowNote] = useState(false);
  const [hotItemId] = useState('0');
  const [assessmentsByChargerId, setAssessmentsByChargerId] = useState<
    Record<string, ChargerAssessment>
  >({});
  const [isAssessing, setIsAssessing] = useState(false);
  const [assessStatus, setAssessStatus] = useState('');

  const [pager, setPager] = useState({ next: '', prev: '', total: 0 });

  useEffect(() => {
    // if (authenticationToken) Get user data
  }, [authenticationToken]);

  useEffect(() => {
    setShowNote(false);
    note.setValue('');
    setAssessmentsByChargerId({});
    setAssessStatus('');
  }, [selectedId]);

  useEffect(() => (note.value ? setShowNote(true) : setShowNote(false)), [note]);

  // let radius = queryParams.max_distance;
  // let matches = [];
  // let disableClick = false;
  // let showRadius = queryParams.use_ecomov === "true"

  const fetchInfo = async ({
    prev_cursor,
    next_cursor,
  }: {
    prev_cursor?: string;
    next_cursor?: string;
  }) => {
    setIsLoading(true);
    if (abortController.current) abortController.current.abort();

    const params = {
      // @mutates
      max_distance: queryParams.max_distance, // 100m
      use_amenity: queryParams.use_amenity,
      has_amenity: queryParams.has_amenity,
      region_id: queryParams.province_id || queryParams.country_id,
      use_ecomov: queryParams.use_ecomov,
      hide_confirmed: queryParams.hide_confirmed,
      all_ratings: queryParams.all_ratings,
      sid: queryParams.sid,
      lat: queryParams.lat,
      lng: queryParams.lng,
      latspan: queryParams.latspan,
      lngspan: queryParams.lngspan,
      only_empty_email: queryParams.only_empty_email,
      min_rating: queryParams.min_rating,
      prev_cursor,
      next_cursor,
    };

    abortController.current = new AbortController();
    const result: any = await fetch(
      `${process.env.REACT_APP_SC_API_BASE}/admin/getInfo?${buildParams(params)}`,
      {
        signal: abortController.current.signal,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      },
    );
    const resData = await result.json();

    setIsLoading(false);
    setItems(resData?.matches);
    // setMapZoom(REGION_ZOOM_LEVEL);
    setMapCenter(resData?.centerLatLng);
    setRegionCenter(resData?.centerLatLng);
    setSelectedId(queryParams.sid || '');
    setPager({
      prev: resData?.pager?.prev,
      next: resData?.pager?.next,
      total: resData?.pager?.total,
    });
  };

  const goNext = async () => {
    setSelectedChargers([]);
    email.setValue('');
    setShowNote(false);

    const itemsInGroup = groupedItems[propertyFilter as keyof typeof groupedItems];

    const findIndex = itemsInGroup.findIndex((e: Hotel) => e._id === selectedId);

    if (findIndex === itemsInGroup.length - 1) {
      await fetchInfo({ next_cursor: pager.next });
    } else {
      const nextIndex = (findIndex + 1) % itemsInGroup.length;
      setSelectedId(itemsInGroup[nextIndex]._id);
    }
  };

  const updateSupplier = async (_: React.MouseEvent<HTMLButtonElement>, unset = false) => {
    setIsLoading(true);

    if (!selectedId) return;

    if (email.value && !isValidEmail(email.value)) {
      return alert('Invalud email entered.');
    }

    const payload: any = {
      //@mutates
      email: email.value,
      charger_ids: selectedChargers.map((e: Charger) => e._id),
      note: note.value,
    };

    if (unset) {
      payload.unset = true;
    }

    const { token } = user;

    const result: any = await fetch(
      `${process.env.REACT_APP_SC_API_BASE}/admin/supplier/${selectedId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const resData = await result.json();

    setIsLoading(false);

    if (result.status === 200) {
      // @TODO error handling
      const newItems = items.map((e: any) => {
        if (e._id === selectedId) {
          e.meta = { ...resData.value };
        }
        return e;
      });

      setItems(newItems);
      if (!unset) goNext();
    } else {
      console.error(result);
    }
  };

  const handleChargerCardClick = (charger: Charger) => {
    const sel: Charger[] = [...selectedChargers]; // @Mutates

    const existingIndex = sel.findIndex((e: Charger) => e._id === charger._id);
    if (existingIndex < 0) {
      sel.push(charger);
    } else {
      sel.splice(existingIndex, 1);
    }

    setSelectedChargers(sel);
  };

  // const handleChargerHover = useCallback((charger: any) => {
  //   setHotItemId(charger._id);
  // }, []);

  // const handleChargerHoverClear = useCallback(() => {
  //   setHotItemId('');
  // }, []);

  // Init: Fetch items
  useEffect(() => {
    // const isSubscribed = true;

    fetchInfo({}).catch(console.error);
  }, [
    searchParams,
    queryParams.has_amenity,
    queryParams.hide_confirmed,
    queryParams.all_ratings,
    queryParams.max_distance,
    queryParams.province_id,
    queryParams.sid,
    queryParams.use_amenity,
    queryParams.use_ecomov,
    queryParams.only_empty_email,
    queryParams.min_rating,
    queryParams.prev_cursor,
    queryParams.next_cursor,
    user,
  ]);

  // const getMapBounds = (val: any) => {
  //   console.log('getMapBounds', val);
  // };

  // When setting selected
  useEffect(() => {
    const updateSelected = (val: string) => {
      if (!val || !items.length) return;

      const selChargers: Charger[] = [];
      const selectedItem = items.find((e: Hotel) => e._id === val);

      if (!selectedItem) {
        return;
      }

      setMapCenter(geoJsonToLatLng(selectedItem.loc));
      setMapZoom(PROPERTY_ZOOM_LEVEL);

      if (selectedItem.chargers && selectedItem.meta && selectedItem.meta.ids) {
        selectedItem.meta.ids.forEach((id: string) => {
          const foundCharger = selectedItem.chargers.find((c: Charger) => c._id === id);
          if (foundCharger) {
            selChargers.push(foundCharger);
          }
        });
      }

      setSelectedChargers(selChargers);

      if (selectedItem.meta) {
        note.setValue(selectedItem.meta.note || '');
        email.setValue(selectedItem.meta.email || '');
      } else {
        note.setValue('');
        email.setValue('');
      }
    };

    // zoom in on selected property
    updateSelected(selectedId);
  }, [selectedId, items]);

  const selectedItem = items?.find((e: Hotel) => e._id === selectedId);

  const goPrev = async () => {
    setSelectedChargers([]);
    email.setValue('');
    setShowNote(false);

    const findIndex = groupedItems[propertyFilter as keyof typeof groupedItems].findIndex(
      (e: Hotel) => e._id === selectedId,
    );

    if (findIndex === 0) {
      await fetchInfo({ prev_cursor: pager.prev });
    } else {
      let nextIndex = findIndex - 1;
      if (nextIndex < 0)
        nextIndex = groupedItems[propertyFilter as keyof typeof groupedItems].length - 1;
      setSelectedId(groupedItems[propertyFilter as keyof typeof groupedItems][nextIndex]._id);
    }
  };

  const csvUrl = `${process.env.REACT_APP_SC_API_BASE}/admin/getcsv?${searchParams.toString()}`;
  const flatcsvUrl = `${
    process.env.REACT_APP_SC_API_BASE
  }/admin/getcsv?flatten=true&${searchParams.toString()}`;
  const flatAllcsvUrl = `${
    process.env.REACT_APP_SC_API_BASE
  }/admin/get_all_csv?flatten=true&${searchParams.toString()}`;

  const getPlaceDetails = async (sid: string) => {
    setIsLoading(true);

    const result: any = await fetch(
      `${process.env.REACT_APP_SC_API_BASE}/admin/get_place_details?sid=${sid}`,
      {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      },
    );
    const resData = await result.json();

    setIsLoading(false);

    if (result.status === 200) {
      // @TODO error handling
      const newItems = items.map((e: Hotel) => {
        if (e._id === selectedId) {
          e.meta = { ...resData };
        }
        return e;
      });

      setItems(newItems);
    } else {
      console.error(result);
    }
  };

  const assessChargers = async () => {
    if (!selectedId || !selectedItem) return;

    setIsAssessing(true);
    setAssessStatus('Checking…');

    try {
      const result = await fetch(`${process.env.REACT_APP_SC_API_BASE}/admin/assess_chargers`, {
        method: 'POST',
        body: JSON.stringify({ sid: selectedId }),
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      const resData: AssessChargersResponse = await result.json();

      if (!result.ok) {
        setAssessStatus(resData?.error || 'AI check failed');
        return;
      }

      const byId: Record<string, ChargerAssessment> = {};
      (resData.assessments || []).forEach((a) => {
        byId[a.charger_id] = a;
      });
      setAssessmentsByChargerId(byId);

      const autoN = resData.auto_confirmed_ids?.length || 0;
      const reviewN = resData.needs_review_ids?.length || 0;
      const visionNote = resData.vision_available ? '' : ' Vision skipped (no OPENAI_API_KEY).';
      setAssessStatus(`Auto-confirmed ${autoN}, ${reviewN} need review.${visionNote}`);

      const confirmedIds = resData.confirmed_ids || [];
      if (confirmedIds.length) {
        const newItems = items.map((e: Hotel) => {
          if (e._id !== selectedId) return e;
          return {
            ...e,
            meta: {
              ...(e.meta || {}),
              ids: confirmedIds,
              note: e.meta?.note || '',
              email: e.meta?.email || '',
              url: e.meta?.url || '',
              updated: e.meta?.updated,
            },
          } as Hotel;
        });
        setItems(newItems);

        const selChargers = (selectedItem.chargers || []).filter((c) =>
          confirmedIds.includes(c._id),
        );
        // Also keep needs-review candidates selected so admin can confirm easily
        const reviewChargers = (selectedItem.chargers || []).filter((c) =>
          (resData.needs_review_ids || []).includes(c._id),
        );
        const merged = [...selChargers];
        reviewChargers.forEach((c) => {
          if (!merged.some((m) => m._id === c._id)) merged.push(c);
        });
        setSelectedChargers(merged);
      }
    } catch (err) {
      console.error(err);
      setAssessStatus('AI check failed');
    } finally {
      setIsAssessing(false);
    }
  };

  const handlePropertyFilter = useCallback(
    (e: string) => {
      setPropertyFilter(e);
      setSelectedId('');
      setMapZoom(REGION_ZOOM_LEVEL);
      setMapCenter(regionCenter);
    },
    [regionCenter],
  );

  useEffect(() => {
    const unconfirmed = items?.filter((item: Hotel) => !(item.meta && item.meta.ids));

    const confirmed = items?.filter((item: Hotel) => item.meta && item.meta.ids);

    setGroupedItems({
      all: items,
      unconfirmed: unconfirmed,
      confirmed: confirmed,
    });
  }, [items]);

  useEffect(() => {
    if (
      groupedItems[propertyFilter as keyof typeof groupedItems] &&
      groupedItems[propertyFilter as keyof typeof groupedItems].length &&
      !selectedId
    ) {
      setSelectedId(groupedItems[propertyFilter as keyof typeof groupedItems][0]._id);
    }
  }, [groupedItems]);

  return (
    <div className="admin-map">
      <div className="wrap">
        <div className="admin-map__container">
          <aside className="admin-map__filters">
            <AdminSearchFilter user={user} />
            <div className="admin-map__filters-footer">
              {items?.length} matches.
              <a className="" href={flatcsvUrl} target="_blank" rel="noreferrer" title="Flat CSV">
                <Icon.Download className="icon-small icon-right" />
              </a>
              <a className="" href={csvUrl} target="_blank" rel="noreferrer" title="Specified CSV">
                <Icon.Download className="icon-small icon-right" />
              </a>
              <a
                className=""
                href={flatAllcsvUrl}
                target="_blank"
                rel="noreferrer"
                title="Flat CSV All Provinces"
              >
                <Icon.Download className="icon-small icon-right" />
              </a>
            </div>
          </aside>
          <div className="admin-map__content">
            <ToggleButton label="Results view" onChange={handlePropertyFilter}>
              <ToggleButtonItem value={PROPERTY_FILTER.all}>
                {isMobile ? 'All' : 'All properties'}
                <span className="admin-map__toggle-numb admin-map__toggle-numb--grey">
                  {groupedItems.all?.length}/{pager.total}
                </span>
              </ToggleButtonItem>
              <ToggleButtonItem value={PROPERTY_FILTER.unconfirmed}>
                {isMobile ? 'Todo' : 'Unconfirmed'}
                <span className="admin-map__toggle-numb admin-map__toggle-numb--red">
                  {groupedItems.unconfirmed?.length}
                </span>
              </ToggleButtonItem>
              <ToggleButtonItem value={PROPERTY_FILTER.confirmed}>
                {isMobile ? 'Done' : 'Confirmed'}
                <span className="admin-map__toggle-numb admin-map__toggle-numb--green">
                  {groupedItems.confirmed?.length}
                </span>
              </ToggleButtonItem>
            </ToggleButton>

            {isLoading ? (
              <Loader size={32} className="admin-map__loader" bgColor="#f2fbfd" />
            ) : null}

            <div className="admin-map__map-container">
              <div className="search__map-btn-container">
                <button
                  className="search__map-btn search__map-btn--minus"
                  onClick={() => {
                    setMapZoom(Math.max(MIN_MAP_ZOOM, mapZoom - 1));
                  }}
                  type="button"
                >
                  <Icon.Minus className="icon-small" />
                </button>
                <button
                  className="search__map-btn search__map-btn--plus"
                  onClick={() => {
                    setMapZoom(Math.min(MAX_MAP_ZOOM, mapZoom + 1));
                  }}
                  type="button"
                >
                  <Icon.Plus className="icon-small" />
                </button>
              </div>

              <GoogleMap
                onDragEnd={(map: any) => {
                  const mapBounds = map.getBounds();
                  const mapSpan = mapBounds.toSpan();
                  const center = mapBounds.getCenter();
                  const centerLatLng = {
                    lat: center.lat(),
                    lng: center.lng(),
                  };
                  const latSpan = mapSpan.lat();
                  const lngSpan = mapSpan.lng();
                  setLatspan(latSpan);
                  setLngspan(lngSpan);

                  setMapCenter(centerLatLng);
                }}
                zoom={mapZoom}
                minZoom={MIN_MAP_ZOOM}
                maxZoom={MAX_MAP_ZOOM}
                center={mapCenter}
                style={{
                  width: selectedItem && !isMobile ? 'calc(100% - 250px)' : '100%',
                  height: isMobile ? '400px' : '796px',
                  borderRadius: '24px',
                }}
                options={{
                  disableDefaultUI: true,
                  mapTypeId: 'hybrid',
                  zoomControlOptions: {
                    position: window.google.maps.ControlPosition.LEFT_BOTTOM,
                  },
                }}
                onIdle={(map: any) => {
                  const mapBounds = map.getBounds();
                  const mapSpan = mapBounds?.toSpan();

                  const latSpan = mapSpan?.lat();
                  const lngSpan = mapSpan?.lng();
                  setLatspan(latSpan);
                  setLngspan(lngSpan);

                  const currentZoom = map.getZoom?.();
                  if (typeof currentZoom === 'number' && currentZoom !== mapZoom) {
                    setMapZoom(currentZoom);
                  }
                }}
              >
                {groupedItems[propertyFilter as keyof typeof groupedItems]?.map((e: Hotel) => {
                  const itemChargers = e.chargers || [];
                  const itemIsSelected = selectedId === e._id;

                  const chargerMarkers = itemChargers.map((charger: Charger) => (
                    <Marker
                      key={charger._id}
                      type="charger"
                      isSelected={selectedChargers.some((selC) => selC._id === charger._id)}
                      // supplierSelected={
                      //     selectedId
                      // }
                      onClick={() => handleChargerCardClick(charger)}
                      // onMouseEnter={() =>
                      //     handleChargerHover(
                      //         charger
                      //     )
                      // }
                      // onMouseLeave={
                      //     handleChargerHoverClear
                      // }
                      position={geoJsonToLatLng(charger.loc)}
                    />
                  ));

                  const propertyMarker = (
                    <Marker
                      key={e._id}
                      radius={CHARGER_DISTANCE_RADIUS}
                      isSelected={itemIsSelected}
                      onClick={() => {
                        setSelectedId(e._id);
                      }}
                      position={geoJsonToLatLng(e.loc)}
                    />
                  );

                  return chargerMarkers.concat(propertyMarker);
                })}
              </GoogleMap>
            </div>
            {selectedItem ? (
              <div className="admin-map__card">
                <AdminSupplierInfo
                  selectedItem={selectedItem}
                  handleChargerCardClick={handleChargerCardClick}
                  selectedChargers={selectedChargers}
                  note={note}
                  email={email}
                  updateSupplier={updateSupplier}
                  goPrev={goPrev}
                  goNext={goNext}
                  groupedItems={groupedItems}
                  propertyFilter={propertyFilter}
                  showNote={showNote}
                  setShowNote={setShowNote}
                  hotItemId={hotItemId}
                  getPlaceDetails={getPlaceDetails}
                  assessChargers={assessChargers}
                  isAssessing={isAssessing}
                  assessStatus={assessStatus}
                  assessmentsByChargerId={assessmentsByChargerId}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
